import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {environment} from '../../environments/environment';
import {catchError} from 'rxjs/operators';
import {NavigationService} from '../services/nav/navigation.service';
import {JwtService} from '../services/user/jwt.service';
import {TenantService} from '../services/tenant.service';
import {logUtishaError, logUtishaInfo} from '../util/log';

@Injectable({
  providedIn: 'root'
})
export class UnauthorizedInterceptor implements HttpInterceptor {

  constructor(private navService: NavigationService,
              private jwt: JwtService,
              private tenant: TenantService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.startsWith(environment.apiUrl)) {
      // @ts-ignore
      return next.handle(req).pipe(catchError(
        error => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            // Platform endpoints (/platform/...) are intentionally called from
            // public pages and return 401 when not authenticated — that is the
            // expected "not logged in" signal. Skip the reload/login cycle so
            // callers can handle the empty result gracefully without being
            // booted to the login page as a side-effect.
            // The last-active ping (/users/me/active) is a background fire-and-forget
            // call that can legitimately 401 on public pages — never treat it as a
            // session expiry signal.
            const isPlatformEndpoint = req.url.includes('/platform/');
            const isLastActivePing = req.url.includes('/users/me/active');
            const isLogoutCall = req.url.includes('/logout');
            if (!isPlatformEndpoint && !isLastActivePing && !isLogoutCall) {
              this.jwt.refresh()
                .then(() => this.refreshAfterUnauth(req.url))
                .catch((err) => this.handleJwtRefreshError(err));
            }
          }
          return throwError(error);
        }));
    }
    return next.handle(req);
  }

  private handleJwtRefreshError(err: any) {
    if (this.navService.isNavigationAllowed()) {
      logUtishaError('Error during jwt refresh, navigating to login page: ', err);
      // Clear the tenant so the login screen shows the generic Utisha brand,
      // not a school that can no longer be reached.
      this.tenant.clear();
      return this.navService.login();
    } else {
      logUtishaError('Error during jwt refresh, do NOT navigate to login page: ', err);
    }
  }

  private static readonly RELOAD_FLAG_KEY = 'ut_unauth_reload_ts';
  private static readonly RELOAD_GUARD_MS = 8000;

  private refreshAfterUnauth(url: string) {
    if (this.navService.isNavigationAllowed()) {
      const flag = sessionStorage.getItem(UnauthorizedInterceptor.RELOAD_FLAG_KEY);
      const recentReload = flag && (Date.now() - parseInt(flag, 10)) < UnauthorizedInterceptor.RELOAD_GUARD_MS;
      if (recentReload) {
        // Reload didn't fix the 401 — redirect to login instead of looping.
        logUtishaInfo(`Unauthorized (${url}): reload loop detected, navigating to login`);
        sessionStorage.removeItem(UnauthorizedInterceptor.RELOAD_FLAG_KEY);
        this.tenant.clear();
        this.navService.login();
      } else {
        logUtishaInfo(`Unauthorized (${url}): browser refresh page`);
        sessionStorage.setItem(UnauthorizedInterceptor.RELOAD_FLAG_KEY, String(Date.now()));
        window.location.reload();
      }
    } else {
      logUtishaInfo(`Unauthorized (${url}): do NOT refresh page`);
    }
  }

}
